import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useRef, useState, type FormEvent, type ReactElement } from 'react';
import {
  QUICK_REPLY_LIMITS,
  quickReplyBodySchema,
  quickReplyShortcutSchema,
  type QuickReply,
} from '@chatapp-inbox/shared';
import { createQuickReply, deleteQuickReply, isApiError, updateQuickReply } from '../../api';
import { Button } from '../details/Button';
import { StatusRegion } from '../details/StatusRegion';
import { useFocusRescue } from '../details/use-focus-rescue';
import { useStatusMessage } from '../details/use-status-message';
import { quickRepliesKey, useQuickReplies } from '../quick-replies';
import { adminErrorMessage } from './error-message';
import { Field, TextAreaField } from './Field';
import { SectionHeading } from './SectionHeading';
import forms from './forms.module.css';
import section from './Section.module.css';
import styles from './QuickReplies.module.css';

export const SHORTCUT_TAKEN_TEXT = 'Já existe uma resposta rápida com este atalho.';
export const LIMIT_REACHED_TEXT = `O limite de ${QUICK_REPLY_LIMITS.maxCount} respostas rápidas foi atingido. Apague alguma para criar outra.`;

type FieldName = 'shortcut' | 'body';

/** Mensagem da primeira regra que o texto não cumpre (as mesmas do servidor, em português), ou null. */
function validate(shortcut: string, body: string): Partial<Record<FieldName, string>> {
  const found: Partial<Record<FieldName, string>> = {};
  const s = quickReplyShortcutSchema.safeParse(shortcut);
  if (!s.success) found.shortcut = s.error.issues[0]?.message ?? 'Atalho inválido';
  const b = quickReplyBodySchema.safeParse(body);
  if (!b.success) found.body = b.error.issues[0]?.message ?? 'Texto inválido';
  return found;
}

function QuickReplyForm({
  editing,
  onDone,
  onCancel,
}: {
  /** A resposta em edição; `null` = nova. */
  editing: QuickReply | null;
  onDone: (saved: QuickReply) => void;
  onCancel: () => void;
}): ReactElement {
  const queryClient = useQueryClient();
  const headingId = useId();
  const [shortcut, setShortcut] = useState(editing?.shortcut ?? '');
  const [body, setBody] = useState(editing?.body ?? '');
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const refs = useRef<Partial<Record<FieldName, HTMLInputElement | HTMLTextAreaElement | null>>>(
    {},
  );

  // O estado `isPending` da mutação só chega na renderização seguinte: um clique duplo rápido passaria
  // por ele duas vezes. Esta trava é síncrona.
  const inFlight = useRef(false);
  const save = useMutation({
    mutationFn: (input: { shortcut: string; body: string }) =>
      editing ? updateQuickReply(editing.id, input) : createQuickReply(input),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: quickRepliesKey });
      onDone(saved);
    },
    onSettled: () => {
      inFlight.current = false;
    },
    onError: (error) => {
      if (isApiError(error) && error.code === 'shortcut_taken') {
        setErrors({ shortcut: SHORTCUT_TAKEN_TEXT });
        refs.current.shortcut?.focus();
      } else if (isApiError(error) && error.code === 'limit_reached') {
        setFormError(LIMIT_REACHED_TEXT);
      } else if (isApiError(error) && error.code === 'bad_request') {
        setFormError(error.message);
      } else {
        setFormError(adminErrorMessage(error, 'Não foi possível salvar. Tente de novo.'));
      }
    },
  });

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (save.isPending || inFlight.current) return;
    const found = validate(shortcut, body);
    setErrors(found);
    setFormError(null);
    const first = (['shortcut', 'body'] as const).find((k) => found[k]);
    if (first) {
      refs.current[first]?.focus();
      return;
    }
    inFlight.current = true;
    save.mutate({ shortcut, body });
  };

  const title = editing ? `Editar /${editing.shortcut}` : 'Nova resposta rápida';
  return (
    <section aria-labelledby={headingId} className={section.card}>
      <h3 id={headingId} className={section.cardTitle}>
        {title}
      </h3>
      <form
        className={forms.form}
        noValidate
        aria-busy={save.isPending || undefined}
        onSubmit={submit}
      >
        <Field
          ref={(el) => void (refs.current.shortcut = el)}
          label="Atalho"
          hint={`Como a equipe a procura: letras minúsculas sem acento, números, _ e - (até ${QUICK_REPLY_LIMITS.shortcutMax}). Pode digitar com a barra: /ola.`}
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          aria-required={true}
          value={shortcut}
          readOnly={save.isPending}
          error={errors.shortcut}
          onChange={(event) => setShortcut(event.target.value)}
        />
        <TextAreaField
          ref={(el) => void (refs.current.body = el)}
          label="Texto"
          hint="Use {nome} para o primeiro nome do cliente e {atendente} para o nome de quem responde. Sem nome do cliente, o {nome} é retirado junto com o espaço antes dele: escreva por exemplo “Olá {nome}!”."
          rows={5}
          aria-required={true}
          value={body}
          readOnly={save.isPending}
          error={errors.body}
          onChange={(event) => setBody(event.target.value)}
        />
        {formError && (
          <p role="alert" className={forms.alert}>
            {formError}
          </p>
        )}
        <div className={forms.actions}>
          <Button type="submit" variant="primary" busy={save.isPending}>
            {save.isPending ? 'Salvando…' : editing ? 'Salvar alterações' : 'Criar resposta'}
          </Button>
          {editing && <Button onClick={onCancel}>Cancelar</Button>}
        </div>
      </form>
    </section>
  );
}

function Row({
  item,
  onEdit,
  onDeleted,
}: {
  item: QuickReply;
  onEdit: () => void;
  onDeleted: () => void;
}): ReactElement {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const remove = useMutation({
    mutationFn: () => deleteQuickReply(item.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: quickRepliesKey });
      onDeleted();
    },
  });
  return (
    <li className={styles.item}>
      <div className={styles.text}>
        <span className={styles.shortcut} dir="ltr">
          /{item.shortcut}
        </span>
        <p className={styles.body} dir="auto">
          {item.body}
        </p>
      </div>
      {confirming ? (
        <div className={styles.confirm}>
          <p className={forms.hint}>Apagar /{item.shortcut}? Isto não pode ser desfeito.</p>
          <div className={forms.actions}>
            <Button variant="danger" busy={remove.isPending} onClick={() => remove.mutate()}>
              Sim, apagar
            </Button>
            <Button onClick={() => setConfirming(false)}>Cancelar</Button>
          </div>
          {remove.isError && (
            <p role="alert" className={forms.alert}>
              {adminErrorMessage(remove.error, 'Não foi possível apagar. Tente de novo.')}
            </p>
          )}
        </div>
      ) : (
        <div className={forms.actions}>
          <Button onClick={onEdit} aria-label={`Editar /${item.shortcut}`}>
            Editar
          </Button>
          <Button
            variant="dangerOutline"
            onClick={() => setConfirming(true)}
            aria-label={`Apagar /${item.shortcut}`}
          >
            Apagar
          </Button>
        </div>
      )}
    </li>
  );
}

/**
 * Administração → Respostas rápidas (docs/quick-replies.md): a lista que a equipe usa no campo de
 * mensagem. Só administradores chegam aqui.
 */
export function QuickRepliesScreen(): ReactElement {
  const headingId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const status = useStatusMessage();
  const replies = useQuickReplies();
  const [editingId, setEditingId] = useState<string | null>(null);
  // Recomeça o formulário (vazio) a cada resposta salva.
  const [formRound, setFormRound] = useState(0);

  // "Tentar de novo" some quando a lista chega e levaria o foco ao <body>.
  const rescue = useFocusRescue(!replies.isError, headingRef);
  const retry = (): void => {
    rescue.arm();
    void replies.refetch().then((result) => {
      if (result.isError) rescue.disarm();
    });
  };
  const items = replies.data;
  const editing = items?.find((q) => q.id === editingId) ?? null;

  return (
    <section aria-labelledby={headingId} className={section.screen}>
      <SectionHeading id={headingId} ref={headingRef}>
        Respostas rápidas
      </SectionHeading>
      <p className={section.lead}>
        Textos prontos que a equipe insere no campo de mensagem com o botão de raio ou digitando /
        com o campo vazio. A pessoa revisa e envia: nada sai sozinho.
      </p>

      {replies.isPending && (
        <p role="status" className={section.loading}>
          Carregando as respostas rápidas…
        </p>
      )}

      {replies.isError && items === undefined && (
        <div className={section.loadError}>
          <p role="alert" className={section.loadErrorText}>
            Não foi possível carregar as respostas rápidas.{' '}
            {adminErrorMessage(replies.error, 'Tente de novo.')}
          </p>
          <Button busy={replies.isFetching} onClick={retry}>
            Tentar de novo
          </Button>
        </div>
      )}

      {items !== undefined && (
        <>
          {/* `key`: trocar de resposta (ou voltar a "nova") recomeça o formulário do zero. */}
          <QuickReplyForm
            key={`${editing?.id ?? 'new'}-${formRound}`}
            editing={editing}
            onDone={(saved) => {
              setEditingId(null);
              setFormRound((n) => n + 1);
              status.announce(
                editing ? `/${saved.shortcut} atualizada.` : `/${saved.shortcut} criada.`,
              );
            }}
            onCancel={() => setEditingId(null)}
          />

          <section aria-label="Respostas cadastradas" className={section.card}>
            <h3 className={section.cardTitle}>
              Cadastradas ({items.length} de {QUICK_REPLY_LIMITS.maxCount})
            </h3>
            {items.length === 0 ? (
              <p className={forms.hint}>Nenhuma ainda. Crie a primeira acima.</p>
            ) : (
              <ul className={styles.list}>
                {items.map((item) => (
                  <Row
                    key={item.id}
                    item={item}
                    onEdit={() => setEditingId(item.id)}
                    onDeleted={() => {
                      if (editingId === item.id) setEditingId(null);
                      status.announce(`/${item.shortcut} apagada.`);
                    }}
                  />
                ))}
              </ul>
            )}
          </section>
        </>
      )}
      <StatusRegion message={status.message} />
    </section>
  );
}
